// Time formatting utilities
export const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

export const formatTimeDetailed = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

export const formatTimeDisplay = (seconds) => {
  let minutes = Math.floor(seconds / 60);
  let secs = seconds % 60;
  minutes = Math.min(minutes, 99);
  secs = Math.min(secs, 59);
  return `${minutes < 10 ? "0" : ""}${minutes}:${secs < 10 ? "0" : ""}${secs}`;
};

// Time formatting with user preference
export const formatTimeWithPreference = (seconds) => {
  const timeFormat = localStorage.getItem("timeFormat") || "24h";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (timeFormat === "12h") {
    // 12-hour format
    if (hours > 0) {
      const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
      const ampm = hours >= 12 ? "PM" : "AM";
      return `${displayHours}h ${minutes}m ${ampm}`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  } else {
    // 24-hour format (default)
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }
};

export const formatDateTime = (dateString) => {
  console.log(
    "formatDateTime called with:",
    dateString,
    "type:",
    typeof dateString
  );

  if (!dateString) {
    console.log(
      "formatDateTime: No dateString provided, returning 'Unknown date'"
    );
    return "Unknown date";
  }

  const timeFormat = localStorage.getItem("timeFormat") || "24h";
  const date = new Date(dateString);

  console.log(
    "formatDateTime: Created date object:",
    date,
    "isValid:",
    !isNaN(date.getTime())
  );

  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.log(
      "formatDateTime: Invalid date detected, returning 'Invalid date'"
    );
    return "Invalid date";
  }

  if (timeFormat === "12h") {
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } else {
    return date.toLocaleString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
};

export const formatDate = (dateString) => {
  console.log(
    "formatDate called with:",
    dateString,
    "type:",
    typeof dateString
  );

  if (!dateString) {
    console.log("formatDate: No dateString provided, returning 'Unknown date'");
    return "Unknown date";
  }

  const timeFormat = localStorage.getItem("timeFormat") || "24h";
  const date = new Date(dateString);

  console.log(
    "formatDate: Created date object:",
    date,
    "isValid:",
    !isNaN(date.getTime())
  );

  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.log("formatDate: Invalid date detected, returning 'Invalid date'");
    return "Invalid date";
  }

  if (timeFormat === "12h") {
    // USA format: MM/DD/YYYY
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } else {
    // EU format: DD/MM/YYYY
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }
};

// Time parsing utilities
export const parseTimeInput = (timeStr) => {
  // Parse formats like "1Y 6M 1d 4h 30m", "2h 30m", "30m", "45s", "1h 30m 45s"
  const timeStrLower = timeStr.toLowerCase().trim();
  let totalSeconds = 0;

  // Extract years (365.25 days per year to account for leap years)
  const yearsMatch = timeStrLower.match(/(\d+)y/);
  if (yearsMatch) {
    totalSeconds += parseInt(yearsMatch[1]) * 365.25 * 86400;
  }

  // Extract months (uppercase M, 30.44 days per month average)
  const monthsMatch = timeStr.match(/(\d+)M/);
  if (monthsMatch) {
    totalSeconds += parseInt(monthsMatch[1]) * 30.44 * 86400;
  }

  // Extract days
  const daysMatch = timeStrLower.match(/(\d+)d/);
  if (daysMatch) {
    totalSeconds += parseInt(daysMatch[1]) * 86400;
  }

  // Extract hours
  const hoursMatch = timeStrLower.match(/(\d+)h/);
  if (hoursMatch) {
    totalSeconds += parseInt(hoursMatch[1]) * 3600;
  }

  // Extract minutes (lowercase m)
  const minutesMatch = timeStrLower.match(/(\d+)m/);
  if (minutesMatch) {
    totalSeconds += parseInt(minutesMatch[1]) * 60;
  }

  // Extract seconds
  const secondsMatch = timeStrLower.match(/(\d+)s/);
  if (secondsMatch) {
    totalSeconds += parseInt(secondsMatch[1]);
  }

  // If no units specified, assume minutes
  if (
    !yearsMatch &&
    !monthsMatch &&
    !daysMatch &&
    !hoursMatch &&
    !minutesMatch &&
    !secondsMatch
  ) {
    const numberMatch = timeStrLower.match(/(\d+)/);
    if (numberMatch) {
      totalSeconds = parseInt(numberMatch[1]) * 60; // Assume minutes
    }
  }

  return Math.max(0, Math.floor(totalSeconds)); // Ensure non-negative and integer
};

// Input validation utilities
export const validateTaskInput = (taskData) => {
  const errors = [];

  if (!taskData.title || taskData.title.trim().length === 0) {
    errors.push("Task title is required");
  }

  if (taskData.title && taskData.title.trim().length > 100) {
    errors.push("Task title must be less than 100 characters");
  }

  if (taskData.description && taskData.description.length > 1000) {
    errors.push("Task description must be less than 1000 characters");
  }

  if (taskData.category && taskData.category.length > 50) {
    errors.push("Task category must be less than 50 characters");
  }

  if (
    taskData.timeTracked &&
    (taskData.timeTracked < 0 || taskData.timeTracked > 86400)
  ) {
    errors.push("Time tracked must be between 0 and 24 hours");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateProjectInput = (projectData) => {
  const errors = [];

  if (!projectData.name || projectData.name.trim().length === 0) {
    errors.push("Project name is required");
  }

  if (projectData.name && projectData.name.trim().length > 50) {
    errors.push("Project name must be less than 50 characters");
  }

  if (!projectData.color || !/^#[0-9A-F]{6}$/i.test(projectData.color)) {
    errors.push("Project color must be a valid hex color");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Input sanitization utilities
export const sanitizeString = (str, maxLength = null) => {
  if (typeof str !== "string") return "";

  let sanitized = str.trim();

  // Remove potentially dangerous characters
  sanitized = sanitized.replace(/[<>]/g, "");

  // Limit length if specified
  if (maxLength && sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
};

export const sanitizeTaskData = (taskData) => {
  return {
    ...taskData,
    title: sanitizeString(taskData.title, 100),
    description: sanitizeString(taskData.description, 1000),
    category: sanitizeString(taskData.category, 50),
    timeTracked: Math.max(0, Math.min(taskData.timeTracked || 0, 86400)),
  };
};

export const sanitizeProjectData = (projectData) => {
  return {
    ...projectData,
    name: sanitizeString(projectData.name, 50),
    color:
      projectData.color && /^#[0-9A-F]{6}$/i.test(projectData.color)
        ? projectData.color.toUpperCase()
        : "#97feed",
  };
};

// Error handling utilities
export const showErrorNotification = (message, duration = 5000) => {
  // Create notification element
  const notification = document.createElement("div");
  notification.className = "error-notification";
  notification.textContent = message;

  // Style the notification
  Object.assign(notification.style, {
    position: "fixed",
    top: "20px",
    right: "20px",
    backgroundColor: "#dc3545",
    color: "white",
    padding: "12px 20px",
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    zIndex: "10000",
    fontSize: "14px",
    fontWeight: "500",
    maxWidth: "300px",
    wordWrap: "break-word",
  });

  document.body.appendChild(notification);

  // Auto-remove after duration
  setTimeout(() => {
    if (notification.parentNode) {
      notification.parentNode.removeChild(notification);
    }
  }, duration);

  // Allow manual dismissal
  notification.addEventListener("click", () => {
    if (notification.parentNode) {
      notification.parentNode.removeChild(notification);
    }
  });
};

export const showSuccessNotification = (message, duration = 3000) => {
  // Create notification element
  const notification = document.createElement("div");
  notification.className = "success-notification";
  notification.textContent = message;

  // Style the notification
  Object.assign(notification.style, {
    position: "fixed",
    top: "20px",
    right: "20px",
    backgroundColor: "#071952",
    color: "white",
    padding: "12px 20px",
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    zIndex: "10000",
    fontSize: "14px",
    fontWeight: "500",
    maxWidth: "300px",
    wordWrap: "break-word",
  });

  document.body.appendChild(notification);

  // Auto-remove after duration
  setTimeout(() => {
    if (notification.parentNode) {
      notification.parentNode.removeChild(notification);
    }
  }, duration);

  // Allow manual dismissal
  notification.addEventListener("click", () => {
    if (notification.parentNode) {
      notification.parentNode.removeChild(notification);
    }
  });
};
